import { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Drawer, Input, Button, Typography, Divider, Tag, message } from 'antd';
import { CopyOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;
const API_URL = process.env.REACT_APP_API_URL;

const VARIABLES = [
  { key: '%recipient.civilite%', label: 'Civilité' },
  { key: '%recipient.nom%', label: 'Nom' },
  { key: '%recipient.prenom%', label: 'Prénom' },
  { key: '%recipient.departement%', label: 'Département' },
  { key: '%recipient.code_departement%', label: 'Code dept' },
  { key: '[UNSUBSCRIBE_URL]', label: 'Lien désabo' },
];

export default function MessageEditorDrawer({
  open,
  onClose,
  onSaved,
  msg, // null = création, objet = édition
  userId,
  userSignature,
  onSignatureUpdated,
  inline = false, // true = pas de Drawer wrapper (utilisable dans un wizard)
  actionsRef = null, // ref exposant { save } au parent (mode inline)
}) {
  const isEdit = !!msg;
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectVariants, setSubjectVariants] = useState([]);
  const [htmlContent, setHtmlContent] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [signatureEdit, setSignatureEdit] = useState(false);
  const [signatureValue, setSignatureValue] = useState('');
  const [showSource, setShowSource] = useState(false);
  const lastFocusedRef = useRef(null);
  const quillRef = useRef(null);

  const token = localStorage.getItem('prae.jwt');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  useEffect(() => {
    if (!open && !inline) return;
    if (isEdit && msg) {
      setName(msg.name || '');
      setSubject(msg.subject || '');
      const variants =
        typeof msg.subject_variants === 'string'
          ? JSON.parse(msg.subject_variants || '[]')
          : msg.subject_variants || [];
      setSubjectVariants(variants);
      setHtmlContent(msg.html_content || '');
      setFromName(msg.from_name || '');
      setFromEmail(msg.from_email || '');
    } else {
      setName('');
      setSubject('');
      setSubjectVariants([]);
      const greeting =
        '<p>Bonjour %recipient.civilite% %recipient.nom%,</p><p><br></p>';
      const sig = userSignature ? `<p><br></p>${userSignature}` : '';
      setHtmlContent(greeting + sig);
      setFromName('Nicolas');
      setFromEmail('nicolas@client.devislocalise.fr');
    }
  }, [open, isEdit, msg]);

  // Si la signature arrive APRÈS le mount (timing AuthContext),
  // on l'injecte tant que le contenu est encore le greeting initial.
  useEffect(() => {
    if (isEdit) return;
    if (!userSignature) return;
    const greeting =
      '<p>Bonjour %recipient.civilite% %recipient.nom%,</p><p><br></p>';
    setHtmlContent(prev =>
      prev === greeting ? greeting + `<p><br></p>${userSignature}` : prev
    );
  }, [userSignature, isEdit]);

  const handleSave = async () => {
    if (!subject.trim()) {
      message.warning('Veuillez saisir un objet');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim() || null,
        subject: subject.trim(),
        subject_variants: subjectVariants.filter(s => s.trim()),
        html_content: htmlContent,
        from_name: fromName,
        from_email: fromEmail,
      };
      let res;
      if (isEdit) {
        res = await fetch(`${API_URL}/messages/library/${msg.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}/messages/library`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...body, user_id: userId }),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const savedMsg = await res.json().catch(() => null);
      message.success(isEdit ? 'Message mis à jour' : 'Message créé');
      onSaved?.(savedMsg);
      onClose?.();
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (actionsRef) {
    actionsRef.current = { save: handleSave };
  }

  const rewriteUrls = str =>
    str.replace(
      /https?:\/\/(www\.)?devis-localise\.com/gi,
      'https://www.devislocalise.fr'
    );

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
    }),
    []
  );

  const handleSaveSignature = async () => {
    try {
      await fetch(`${API_URL}/me/signature`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ email_signature: signatureValue }),
      });
      message.success('Signature enregistrée');
      setSignatureEdit(false);
      onSignatureUpdated?.(signatureValue);
    } catch {
      message.error('Erreur sauvegarde signature');
    }
  };

  const trackCursor = e => {
    lastFocusedRef.current = {
      element: e.target,
      selectionStart: e.target.selectionStart,
      selectionEnd: e.target.selectionEnd,
    };
  };

  const insertVariable = v => {
    // Try inserting into Quill editor first
    const editor = quillRef.current?.getEditor?.();
    if (editor && !showSource) {
      const range = editor.getSelection();
      if (range) {
        editor.insertText(range.index, v);
        editor.setSelection(range.index + v.length);
        return;
      }
    }

    const ref = lastFocusedRef.current;
    if (!ref?.element) {
      navigator.clipboard.writeText(v);
      message.success(`${v} copié`);
      return;
    }

    const el = ref.element;
    const start = ref.selectionStart ?? el.value.length;
    const end = ref.selectionEnd ?? start;
    const before = el.value.substring(0, start);
    const after = el.value.substring(end);
    const newValue = before + v + after;

    const nativeInputValueSetter =
      Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      )?.set ||
      Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(el, newValue);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const newPos = start + v.length;
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(newPos, newPos);
      lastFocusedRef.current = {
        element: el,
        selectionStart: newPos,
        selectionEnd: newPos,
      };
    }, 0);
  };

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Text
          strong
          style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
        >
          Nom du message
        </Text>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ex: Relance nettoyage PACA"
        />
      </div>

      <div>
        <Text
          strong
          style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
        >
          Objet principal
        </Text>
        <Input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          onSelect={trackCursor}
          onClick={trackCursor}
          placeholder="Objet du message"
        />
        {subjectVariants.map((v, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 6,
              alignItems: 'center',
            }}
          >
            <Input
              value={v}
              onChange={e => {
                const next = [...subjectVariants];
                next[i] = e.target.value;
                setSubjectVariants(next);
              }}
              placeholder={`Variante ${i + 1}`}
              onSelect={trackCursor}
              onClick={trackCursor}
              style={{ flex: 1 }}
            />
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() =>
                setSubjectVariants(prev => prev.filter((_, j) => j !== i))
              }
            />
          </div>
        ))}
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setSubjectVariants(prev => [...prev, ''])}
          style={{ marginTop: 6 }}
        >
          Ajouter une variante
        </Button>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          Un objet sera choisi au hasard parmi les variantes à chaque envoi.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Expéditeur (nom)
          </Text>
          <Input
            value={fromName}
            onChange={e => setFromName(e.target.value)}
            placeholder="Nicolas"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Expéditeur (email)
          </Text>
          <Input
            value={fromEmail}
            onChange={e => setFromEmail(e.target.value)}
            placeholder="contact@example.com"
          />
        </div>
      </div>

      <Divider style={{ margin: '4px 0' }} />

      {/* Variables */}
      <div>
        <Text
          strong
          style={{ fontSize: 12, marginBottom: 6, display: 'block' }}
        >
          Variables (cliquer pour copier)
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {VARIABLES.map(v => (
            <Tag
              key={v.key}
              icon={<CopyOutlined />}
              style={{
                cursor: 'pointer',
                fontSize: 11,
                userSelect: 'none',
              }}
              onClick={() => insertVariable(v.key)}
            >
              {v.label}
            </Tag>
          ))}
        </div>
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <Text strong style={{ fontSize: 12 }}>
            Contenu
          </Text>
          <Button
            size="small"
            type="link"
            onClick={() => setShowSource(!showSource)}
          >
            {showSource ? 'Éditeur visuel' : 'Code source'}
          </Button>
        </div>
        {showSource ? (
          <TextArea
            value={htmlContent}
            onChange={e => setHtmlContent(rewriteUrls(e.target.value))}
            placeholder="<html>..."
            rows={16}
            onSelect={trackCursor}
            onClick={trackCursor}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={htmlContent}
            onChange={v => setHtmlContent(rewriteUrls(v))}
            modules={quillModules}
            style={{ minHeight: 250 }}
          />
        )}
      </div>

      <Divider style={{ margin: '4px 0' }} />

      {/* Signature */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <Text strong style={{ fontSize: 12 }}>
            Signature par défaut
          </Text>
          {!signatureEdit ? (
            <Button
              size="small"
              type="link"
              onClick={() => {
                setSignatureValue(userSignature || '');
                setSignatureEdit(true);
              }}
            >
              Modifier
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: 4 }}>
              <Button size="small" onClick={handleSaveSignature}>
                Enregistrer
              </Button>
              <Button size="small" onClick={() => setSignatureEdit(false)}>
                Annuler
              </Button>
            </div>
          )}
        </div>
        {signatureEdit ? (
          <TextArea
            value={signatureValue}
            onChange={e => setSignatureValue(e.target.value)}
            rows={5}
            placeholder="<p>Cordialement,<br/>Nicolas Mintz</p>"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : userSignature ? (
          <iframe
            srcDoc={`<style>body{font-family:Arial,sans-serif;font-size:13px;margin:8px;color:#555}p{margin:0}</style>${userSignature}`}
            sandbox=""
            title="Signature"
            style={{
              width: '100%',
              height: 80,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              background: '#fafafa',
            }}
          />
        ) : (
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 12,
              color: '#94a3b8',
            }}
          >
            Aucune signature définie
          </div>
        )}
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          Insérée automatiquement dans chaque nouveau message.
        </div>
      </div>
    </div>
  );

  if (inline) return body;

  return (
    <Drawer
      title={isEdit ? 'Modifier le message' : 'Nouveau message'}
      open={open}
      onClose={onClose}
      width={640}
      extra={
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          style={{ background: '#f97316', borderColor: '#f97316' }}
        >
          {isEdit ? 'Mettre à jour' : 'Créer'}
        </Button>
      }
    >
      {body}
    </Drawer>
  );
}
